<script setup lang="ts">
type EnumData = App.Base.Data.EnumData;

import type { HybridlyFormData } from '@/@types/global';

import { ButtonColor, ButtonVariant } from '@/hooks/useButtonClasses';

import Button from '@/components/button/Button.vue';
import DangerDialog from '@/components/dialog/DangerDialog.vue';
import Form from '@/components/form/Form.vue';
import FormFooter from '@/components/formFooter/FormFooter.vue';
import FormHeader from '@/components/formHeader/FormHeader.vue';
import TestFormFields from './TestFormFields.vue';

const props = defineProps<{
  isEditing?: boolean;
  form: HybridlyFormData<{
    type: EnumData;
    subject: EnumData;
    test: EnumData;
    date: string;
    scores: Record<string, number>;
    is_waiver_assisted: boolean;
    is_registered: boolean;
    is_waived: boolean;
  }>;
  returnUrl?: string;
  types: EnumData[];
  scoreMap: Record<string, Record<string, { rules?: string[]; step?: number }>>;
  labelMap: Record<string, string>;
  customTests: App.Configuration.Data.CustomTestData[];
  apCourses: EnumData[];
  student: App.Students.Data.StudentData;
}>();

const isCancelDialogOpen = ref(false);

const { onBackForward } = useBackForward();

// TODO: Fix intecepting the back button
onBackForward(() => {
  router.abort();
});

function handleToggleCancelDialog() {
  if (props.form.isDirty) {
    isCancelDialogOpen.value = !isCancelDialogOpen.value;
    return;
  }
  handleRedirect();
}

function handleRedirect() {
  router.navigate({
    url: props.returnUrl || route('students.tests.index', { student: props.student.id })
  });
}
</script>

<template>
  <Form @submit.prevent="form.submit">
    <template #header>
      <FormHeader
        canClose
        :title="`${isEditing ? 'Edit' : 'Add'} a Exam`"
        :description="
          isEditing
            ? 'Use the form below to edit the exam.'
            : 'Fill out the form below add a new exam.'
        "
        @onClose="handleToggleCancelDialog"
      />
    </template>
    <div class="flex flex-col gap-y-8">
      <TestFormFields
        :form="form"
        :types="types"
        :scoreMap="scoreMap"
        :labelMap="labelMap"
        :customTests="customTests"
        :apCourses="apCourses"
      />
    </div>
    <template #footer>
      <FormFooter>
        <Button
          type="submit"
          :color="ButtonColor.primary"
          :isDisabled="form.processing || !form.isDirty"
          :isLoading="form.processing"
          :variant="ButtonVariant.solid"
          >Confirm</Button
        >
        <Button
          :color="ButtonColor.slate"
          :variant="ButtonVariant.outlined"
          type="button"
          @click="handleToggleCancelDialog"
        >
          Back to exams
        </Button>
      </FormFooter>
    </template>
  </Form>
  <DangerDialog
    :title="`Cancel ${isEditing ? 'editing' : 'adding a new'} exam`"
    :message="`Are you sure you want to cancel ${isEditing ? 'editing' : 'adding'} this exam? All ${
      isEditing ? 'changes' : 'information'
    } will be lost.`"
    :isOpen="isCancelDialogOpen"
    cancelButtonLabel="Stay on this page"
    confirmButtonLabel="Go back to exams"
    @onCancel="handleToggleCancelDialog"
    @onClose="handleToggleCancelDialog"
    @onConfirm="handleRedirect"
  />
</template>
